import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ReactSVG } from "react-svg";
import { FaRegTrashAlt, FaSpinner } from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../../api/api";
import { IoIosArrowBack } from "react-icons/io";
import { useTranslation } from "react-i18next";
import { FaPencil } from "react-icons/fa6";
import { useProfileStore } from "../../store/useUserStore";
import { motion, AnimatePresence } from "framer-motion";

interface Month {
    id: string;
    name: string;
    date: Date;
}

const Cart = () => {
    let cartTotalPrice = 0;
    const [cartItems, setCartItems] = useState([])
    const [innerLoading, setInnerLoading] = useState(false)
    const navigate = useNavigate()
    const { t } = useTranslation()
    const [notificationCount, setNotificationCount] = useState(0)
    const { setHomePageShow, setSelectedStudent, setCartEdit, setLoading } = useProfileStore()

    // State to manage timeslots schedules modal
    const [openScheduleModal, setOpenScheduleModal] = useState(false)
    const [selectedScheduleList, setSelectedScheduleList] = useState([])

  useEffect(() => {
    getCartItems();
    getOrderNotificationCount();
  }, []);

    function getOrderNotificationCount() {
        // get payment notification count
        api.get(`${process.env.PUBLIC_URL}/order/notification/summary`).then(res => {
            console.log("res notif", res)
            setNotificationCount(res.data.data.seen_count)
        }).catch(err =>{
            toast.error(t("server_error"))
            console.log("err", err)
        })
    }
    const [waivedSet, setWaivedSet] = useState<Set<string>>(new Set())

  function getCartItems() {
    setLoading(true)
    api
      .get(`${process.env.REACT_APP_API_BASE_URL}/cart`)
      .then((res) => {
        setLoading(false)
        console.log("res cart", res.data.data);
        setCartItems(res.data.data);

        const newWaivedSet = new Set<string>();
        res.data.data.forEach((student) => {
          student.cart.forEach((item) => {
            if (item.type === "wave") {
              if (item.payment_method === "month") {
                const key = `${item.course_branch_id}_${item.selected_date}`;
                newWaivedSet.add(key);
              } else if (item.payment_method == "attandance") {
                const key = `${item.course_branch_id}_${item.selected_package.id}`;
                newWaivedSet.add(key);
              } else if (item.payment_method == "attandance-no replace") {
                const key = `${item.course_branch_id}_${item.selected_package.id}`;
                newWaivedSet.add(key);
              }
            }
          });
        });
        setWaivedSet(newWaivedSet);
      })
      .catch((err) => {
        setLoading(false)
        console.log("err cart", err);
      });
  }

  function deleteCartItem(cartItem: any) {
    console.log("deleteCartItem");
    setInnerLoading(true);
    api
      .delete(`${process.env.PUBLIC_URL}/cart/delete/${cartItem.id}`)
      .then((res) => {
        setInnerLoading(false);
        toast.success("Delete from cart");
        console.log("res", res);
        getCartItems();
      })
      .catch((err) => {
        setInnerLoading(false);
        toast.error(t("server_error"));
        console.log("err", err);
      });
  }

  function handleCheckout() {
    console.log("handleCheckout");
    setLoading(true)
    api
      .post(`${process.env.PUBLIC_URL}/cart/checkout`)
      .then((res) => {
        console.log("res", res);

        if (res.data.skip_payment === true) {
          toast.success(t("cart.payment_success"));
          return getCartItems();
        }

        const paymentUrl = res.data.payment_url;
        if (paymentUrl) {
          const windowReference = window.open();
          if (windowReference) {
            windowReference.location = paymentUrl;
          } else {
            toast.error(t("cart.popup_blocked")); // handle popup block
          }
          return getCartItems();
        } else {
          toast.error(t("url_not_found"));
        }
      })
      .catch((err) => {
        toast.error(t("server_error"));
        console.log("err", err);
      })
      .finally(() => {
        setLoading(false)
      });
  }

  const [showMonthModal, setShowMonthModal] = useState(false);
  const [selectedCartItem, setSelectedCartItem] = useState<any>(null);
  const [selectedMonths, setSelectedMonths] = useState<string[]>([]);

  const getMonths = (): Month[] => {
    const months: Month[] = [];

    const startDate = new Date();
    startDate.setDate(1); // Start from the first day of the current month
    startDate.setMonth(startDate.getMonth() - 6); // Move 6 months back

    for (let i = 0; i < 24; i++) {
      // Total 24 months (6 past + current + 17 future)
      const month = new Date(startDate);
      month.setMonth(startDate.getMonth() + i);

      const year = month.getFullYear();
      const monthNumber = String(month.getMonth() + 1).padStart(2, "0");

      months.push({
        id: `${year}-${monthNumber}`,
        name: month.toLocaleString("default", {
          month: "short",
          year: "numeric",
        }),
        date: month,
      });
    }

    return months;
  };

  const months: Month[] = getMonths();

  const handleSelectMonth = (month: Month) => {
    setSelectedMonths((prev: any) =>
      prev.includes(month.id)
        ? prev.filter((id: any) => id !== month.id)
        : [...prev, month.id],
    );
  };
  const isChecked = (month: Month) => {
    return selectedMonths ? selectedMonths.includes(month.id) : false;
  };

  useEffect(() => {
    // Parse "Oct 2024" into a Date object (if selected_date exists)
    if (selectedCartItem?.selected_date) {
      const parsed = new Date(`${selectedCartItem.selected_date} 01`);
      const year = parsed.getFullYear();
      const month = String(parsed.getMonth() + 1).padStart(2, "0");
      const selectedId = `${year}-${month}`;

      setSelectedMonths((prev: string[]) => {
        if (!prev.includes(selectedId)) {
          return [...prev, selectedId];
        }
        return prev;
      });
    }
  }, [selectedCartItem]);

  function navigateCourseView(
    student_id: number,
    student_name: string,
    course_branch_id: number,
  ) {
    setHomePageShow("school");
    setSelectedStudent({ id: student_id, name: student_name })
    // setLastVisitedCourseId(course_branch_id.toString())
    return navigate(`/course/${course_branch_id}?from=cart`)
  }

  function navigateItemView(
    student_id: number,
    student_name: string,
    item_id: number,
    course_branch_id: number,
  ) {
    setHomePageShow("school");
    setSelectedStudent({ id: student_id, name: student_name });
    return navigate(
      `/item/${item_id}?course_branch_id=${course_branch_id}&from=cart`,
    );
  }

    const formatTimeSlotList = (slots: { day: string; start_time: string; end_time: string }[]): string => {
        const format12Hour = (time: string): string => {
            const [hour, minute] = time.split(":").map(Number)
            const date = new Date()
            date.setHours(hour, minute)
            return date.toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
            })
        }

        const shortDay = (day: string): string => {
            return day.slice(0, 3)
        }

        return slots
            .map(slot => {
                const start = format12Hour(slot.start_time)
                const end = format12Hour(slot.end_time)
                return `${start} - ${end} ${shortDay(slot.day)}`
            }).join(", ")
    }


  return (
    <div className="pb-20">
      <div className="mb-2 flex items-center justify-between pl-2 pr-3">
        <IoIosArrowBack size={28} color="#000" onClick={() => navigate("/")} />
        <div className="text-lg font-medium flex-1 text-center">
          {t("cart.cart")}
        </div>
        <div className="relative">
          <ReactSVG
            src={
              process.env.PUBLIC_URL + "/assets/img/icons/payment-history.svg"
            }
            onClick={() => navigate("/payment-history")}
            className="ml-auto"
          />

          {/* Red Notification Badge */}
          {notificationCount > 0 && (
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full"></span>
          )}
        </div>
      </div>

      <div className="mt-8 px-3">
        {cartItems && cartItems.length >= 1 ? (
          <>
            {cartItems.map((student: any, key: any) => {
              return (
                <div className="border-b border-gray-300 pb-8">
                  <div key={key} className="text-lg font-semibold mt-3">
                    {student.student_name}
                  </div>
                  {student.cart.map((cartItem: any, index: number) => {
                    if (cartItem.type === "course") {
                      // let isWaived = false
                      // if (cartItem.pricing_method === "Month") {
                      //     isWaived = waivedSet.has(`${cartItem.course_branch_id}_${cartItem.selected_date}`);
                      // } else if (cartItem.pricing_method === "Attandance") {
                      //     isWaived = waivedSet.has(`${cartItem.course_branch_id}_${cartItem.selected_package.id}`);
                      // }  else if (cartItem.pricing_method === "Attandance-No replace") {
                      //     isWaived = waivedSet.has(`${cartItem.course_branch_id}_${cartItem.selected_package.id}`);
                      // }

                      // if (isWaived) {
                      //     return (
                      //
                      //     )
                      // } else {
                      cartTotalPrice += cartItem.price;
                      return (
                        <div className="grid grid-cols-5 gap-3 mt-5" key={index} >
                          <div className="col-span-1 self-center">
                            {cartItem.course.images &&
                            cartItem.course.images.length > 0 ? (
                              <img src={cartItem.course.images[0]} />
                            ) : (
                              <img src={"/assets/img/products/product1.png"} />
                            )}
                          </div>
                          <div className="col-span-4">
                            <div className="grid grid-cols-3 gap-1">
                              <div className="col-span-2">
                                <div
                                  onClick={() =>
                                    navigateCourseView(
                                      student.student_id,
                                      student.student_name,
                                      cartItem.course_branch_id,
                                    )
                                  }
                                  className="text-lg font-medium truncate"
                                >
                                  {cartItem.course.name}
                                </div>
                                <div className="truncate" onClick={() => {
                                    setSelectedScheduleList(cartItem.schedules)
                                    setOpenScheduleModal(true)
                                }}>{formatTimeSlotList(cartItem.schedules)}</div>
                              </div>
                              <div className="flex flex-col col-span-1">
                                <div className="self-end">
                                  <div
                                    className={`rounded-xl text-center p-[0.12rem] px-2 text-xs truncate ${
                                      cartItem.status === "added"
                                        ? "bg-green-500 text-white"
                                        : cartItem.status === "overdue"
                                        ? "bg-red-500 text-white"
                                        : "bg-amber-600 text-white"
                                    }`}
                                  >
                                    {cartItem.status === "added"
                                      ? t("cart.newly_added")
                                      : cartItem.status === "overdue"
                                      ? t("cart.overdue")
                                      : t("cart.due_soon")}
                                  </div>
                                </div>
                                <div className="self-end mt-2">
                                  {cartItem.status !== "overdue" &&
                                    (innerLoading ? (
                                      <FaSpinner className="animate-spin" />
                                    ) : (
                                      <FaRegTrashAlt
                                        size={16}
                                        className="mt-1 cursor-pointer"
                                        onClick={() => deleteCartItem(cartItem)}
                                      />
                                    ))}
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-8 mt-2">
                              <div className=" whitespace-normal break-words col-span-5 flex justify-between items-center">
                                {cartItem.pricing_method === "Month"
                                  ? cartItem.selected_date
                                  : cartItem.selected_package.count + " attd"}
                                {cartItem.status !== "overdue" && (
                                  <FaPencil
                                    className="cursor-pointer"
                                    onClick={() => {
                                        setCartEdit(cartItem)
                                        return navigate(`/course/${cartItem.course_branch_id}/edit`)
                                    }}
                                  />
                                )}
                              </div>
                              <div className=" col-span-3 text-end font-medium">{`RM ${cartItem.price}`}</div>
                            </div>
                          </div>
                        </div>
                      );
                    } else if (cartItem.type === "wave") {
                      cartTotalPrice -= cartItem.price;
                      return (
                        <div className="grid grid-cols-5 gap-3 mt-5" key={index} >
                          <div className="col-span-1 self-center">
                            {cartItem.course.images &&
                            cartItem.course.images.length > 0 ? (
                              <img src={cartItem.course.images[0]} />
                            ) : (
                              <img src={"/assets/img/products/product1.png"} />
                            )}
                          </div>
                          <div className="col-span-4">
                            <div className="grid grid-cols-3 gap-1">
                              <div className="col-span-2">
                                <div
                                  onClick={() =>
                                    navigateCourseView(
                                      student.student_id,
                                      student.student_name,
                                      cartItem.course_branch_id,
                                    )
                                  }
                                  className="text-lg font-medium truncate"
                                >
                                  Waive Payment
                                </div>
                                <div>{cartItem.course.name}</div>
                              </div>
                            </div>

                            <div className="grid grid-cols-8 mt-2">
                              <div className=" whitespace-normal break-words col-span-5 flex justify-between items-center">
                                {cartItem.selected_date}
                              </div>
                              <div className=" col-span-3 text-end text-red-600 font-medium">{`- RM ${cartItem.price}`}</div>
                            </div>
                          </div>

                          <div className="col-span-5 p-2 border border-1 border-gray-300 rounded">
                            <span className="text-theme">
                              {cartItem.remark}
                            </span>
                          </div>
                        </div>
                      );
                    } else if (cartItem.type == "item") {
                        cartTotalPrice += cartItem.item.price;
                        return (
                            <div className="grid grid-cols-5 gap-3 mt-5" key={index} >
                                <div className="col-span-1 self-center">
                                    {cartItem.item.images && cartItem.item.images.length > 0 ? (
                                        <img src={cartItem.item.images[0]} />
                                    ):(
                                        <img src={"/assets/img/products/product1.png"} />
                                    )}
                                </div>
                                <div className="col-span-4">
                                    <div className="grid grid-cols-3 gap-1">
                                        <div className="col-span-2 ">
                                            <div className="text-lg font-medium truncate" onClick={() => navigateItemView( student.student_id, student.student_name, cartItem.item.id, cartItem.item.course_branch_id, )}>{cartItem.item.name}</div>
                                        </div>
                                        <div className="col-span-1">
                                            <div className="justify-items-end">
                                            {innerLoading ? (
                                                <FaSpinner className="animate-spin" />
                                            ) : (
                                                <FaRegTrashAlt size={18} className="justify-self-end mt-1" onClick={() => { deleteCartItem(cartItem); }} />
                                            )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-2">
                                        <div className="whitespace-normal break-words"></div>
                                        <div className="text-end text-lg font-medium">{`RM ${cartItem.item.price}`}</div>
                                    </div>
                                </div>
                            </div>
                        );
                    }
                  })}
                </div>
              );
            })}

            <div className="pt-8 pb-8">
                <div className="max-w-screen-md mx-auto px-4">
                    <div className="text-base font-medium mb-3">
                        {t("cart.sub_total")}
                        <span className="float-right">RM {cartTotalPrice.toFixed(2)}</span>
                    </div>

                    <h4 className="text-[20px] font-medium mb-6">
                        {t("cart.grand_total")}
                        <span className="float-right">RM {cartTotalPrice.toFixed(2)}</span>
                    </h4>

                    <button onClick={handleCheckout} type="button" className="text-white text-[16px] leading-none w-full py-[15px] bg-theme border-none rounded-3xl flex items-center justify-center" >
                    {innerLoading ? <FaSpinner className="animate-spin" /> : t("cart.proceed_to_checkout")}
                    </button>
                </div>
            </div>

          </>
        ) : (
            <div className="flex flex-col items-center justify-center text-center mt-[200px]">
                <div className="w-[100px] h-[100px] mb-5 mx-auto">
                    <ReactSVG src={process.env.PUBLIC_URL + "/assets/img/icons/cart.svg"} className="w-full h-full" />
                </div>
                <div>
                    <p>
                    {t("cart.no_items_in_cart")}{" "}
                    <Link to={process.env.PUBLIC_URL + "/"} className="block underline font-medium">
                        {t("cart.add_some")}
                    </Link>
                    </p>
                </div>
            </div>
        )}
      </div>

      {showMonthModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white px-6 py-2 rounded-lg w-5/6 max-w-md min-h-[300px] flex flex-col justify-between">
            <div>
              <div className="text-lg font-semibold mb-4">
                {t("cart.edit_payment_term")}
              </div>
              <div
                className="mt-2 bg-white rounded-md w-full z-10"
                style={{ maxHeight: "13rem", overflowY: "auto" }}
              >
                {months.map((month) => (
                  <div
                    key={month.id}
                    className={`flex items-center px-1 py-1 hover:bg-gray-100 cursor-pointer`}
                    onClick={() => {
                      handleSelectMonth(month);
                    }}
                  >
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={isChecked(month)}
                      readOnly
                    />
                    <span className="text-lg">{month.name}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowMonthModal(false);
                  setSelectedMonths([]);
                }}
                className="px-4 py-1 text-gray-700 rounded-full hover:bg-gray-100 transition"
              >
                {t("close")}
              </button>
              <button
                className="px-4 py-1 bg-theme text-white rounded-full transition"
                onClick={() => {
                  setShowMonthModal(false);
                  setSelectedMonths([]);
                }}
              >
                {t("save")}
              </button>
            </div>
          </div>
        </div>
      )}


      <AnimatePresence>
        {openScheduleModal && (
        <div className="fixed inset-0 z-10 bg-black bg-opacity-50 flex justify-center items-end" onClick={() => {
            setOpenScheduleModal(false)
            setSelectedScheduleList([])
        }}>
            <motion.div
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "100%" }}
                transition={{ stiffness: 100 }}
                className="w-full max-w-[648px] bg-white shadow-2xl rounded-t-3xl px-4 pt-4 pb-20"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="mb-10">
                   {selectedScheduleList.map((slot:any, index) => {
                        const format12 = (time: string) => {
                            const [h, m] = time.split(":").map(Number)
                            const d = new Date()
                            d.setHours(h, m)
                            return d.toLocaleTimeString("en-US", {
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                            })
                        }

                        return (
                            <div key={index}>
                                {format12(slot?.start_time)} - {format12(slot?.end_time)} {slot.day}
                            </div>
                        )
                    })}
                </div>
            </motion.div>
        </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Cart;
